import classNames from "classnames";
import { ProfileItem } from "PFApp/components/profile_item";
import { ProfileSource, ProfileSourceToggle } from "PFApp/components/profile_source_toggle";
import { SelectV2 } from "PFComponents/select_v2";
import { getProfileName } from "PFCore/helpers/profile";
import { Collection } from "PFTypes";
import { Option as SelectV2Option } from "PFTypes/options";
import { CSSProperties } from "react";
import { useTranslation } from "react-i18next";

import { DisplayOption } from "./display_option";
import css from "./matches_named_resources_selector.module.scss";
import { CostField, Option } from "./matches_named_resources_selector.types";
import { OptionsTableHeader } from "./options_table_header";

type MatchesNamedResourcesSelectorProps = {
  profilesSelector: ProfileSource | null;
  onProfilesSelectorChange: (value: ProfileSource) => void;
  onNamedResourcesChange: (options: Option[]) => void;
  onMatchesChange: (options: Option[]) => void;
  onShortlistChange?: (options: Option[]) => void;
  fetchNamedResourcesOptions: (term?: string) => Promise<Collection<Option[]>>;
  fetchMatchesOptions: (term?: string) => Promise<Collection<Option[]>>;
  fetchShortlistOptions?: (term?: string) => Promise<Collection<Option[]>>;
  allowedSources?: ProfileSource[];
  matchesDisabled?: boolean;
  namedResourceDisabled?: boolean;
  shortlistDisabled?: boolean;
  matchesNotReady?: boolean;
  classes?: { root?: string; select?: string; namedResourceDropdown?: string };
  styles?: { root?: CSSProperties };
  costField?: CostField;
  displayNamedResourceJobTitle?: boolean;
};

const DEFAULT_ALLOWED_SOURCES: ProfileSource[] = [
  ProfileSource.Matches,
  ProfileSource.Shortlist,
  ProfileSource.NamedResource
];

export const MatchesNamedResourcesSelector = ({
  profilesSelector,
  onProfilesSelectorChange,
  onNamedResourcesChange,
  onMatchesChange,
  onShortlistChange,
  fetchNamedResourcesOptions,
  fetchMatchesOptions,
  fetchShortlistOptions,
  allowedSources = DEFAULT_ALLOWED_SOURCES,
  matchesDisabled,
  namedResourceDisabled,
  shortlistDisabled,
  matchesNotReady,
  classes = {},
  styles = {},
  costField,
  displayNamedResourceJobTitle
}: MatchesNamedResourcesSelectorProps) => {
  const { t } = useTranslation();

  return (
    <div className={classNames(css.root, classes.root)} style={styles.root}>
      {profilesSelector === ProfileSource.NamedResource && (
        <SelectV2
          title={t("workforceMember")}
          fetchOptions={async ({ searchTerm }) => {
            const result = await fetchNamedResourcesOptions(searchTerm);
            return {
              entries: result.entries.map((profile) => ({
                id: profile.id,
                displayElement: (
                  <ProfileItem profile={profile} displayJobTitle={displayNamedResourceJobTitle} />
                ),
                value: getProfileName(profile),
                original: profile
              })),
              meta: result.meta
            };
          }}
          onChange={({ original }: SelectV2Option<any>) => onNamedResourcesChange([original])}
          value={undefined}
          disabled={namedResourceDisabled}
          dropdownWidth={300}
          className={classes.select}
        />
      )}
      {profilesSelector === ProfileSource.Matches && (
        <SelectV2
          title={t("workforceMember")}
          fetchOptions={async ({ searchTerm }) => {
            const result = await fetchMatchesOptions(searchTerm);
            return {
              entries: result.entries.map((profile) => ({
                id: profile.id,
                displayElement: <DisplayOption option={profile} costField={costField} />,
                value: getProfileName(profile),
                original: profile
              })),
              meta: result.meta
            };
          }}
          onChange={({ original }: SelectV2Option<any>) => onMatchesChange([original])}
          value={undefined}
          disabled={matchesDisabled}
          dropdownWidth="fit-content"
          className={classes.select}
          noOptionsText={t("profilesSelector.noMatches")}
          dropdownHeaderElement={(options) =>
            options.length > 0 && <OptionsTableHeader costField={costField} />
          }
        />
      )}
      {profilesSelector === ProfileSource.Shortlist && !!fetchShortlistOptions && (
        <SelectV2
          title={t("workforceMember")}
          fetchOptions={async ({ searchTerm }) => {
            const result = await fetchShortlistOptions(searchTerm);
            return {
              entries: result.entries.map((profile) => ({
                id: profile.id,
                displayElement: <DisplayOption option={profile} costField={costField} />,
                value: getProfileName(profile),
                original: profile
              })),
              meta: result.meta
            };
          }}
          onChange={({ original }: SelectV2Option<any>) => onShortlistChange?.([original])}
          value={undefined}
          disabled={shortlistDisabled}
          dropdownWidth="fit-content"
          className={classes.select}
          noOptionsText={t("profilesSelector.noMatches")}
          dropdownHeaderElement={(options) =>
            options.length > 0 && <OptionsTableHeader costField={costField} />
          }
        />
      )}
      <ProfileSourceToggle
        value={profilesSelector}
        onChange={onProfilesSelectorChange}
        allowedSources={allowedSources}
        disabledSources={{
          [ProfileSource.Matches]: {
            disabled: !!matchesDisabled,
            tooltip: matchesNotReady ? t("profilesSelector.matchesNotReady") : undefined
          },
          [ProfileSource.NamedResource]: { disabled: !!namedResourceDisabled },
          [ProfileSource.Shortlist]: {
            disabled: !!shortlistDisabled,
            tooltip: shortlistDisabled ? t("profilesSelector.shortlistDisabledNoShortlists") : undefined
          }
        }}
        fluid
        className={css.profileSourceToggle}
      />
    </div>
  );
};
